/**
 * "Elkészült az autója": the notice to the customer that the car is ready (owner's design, 2026-11-04), without
 * closing the work order. Pure helpers shared by the app, the tests and the docs: the amount (rounded, always
 * "approx."), the opening hours, the route links to the workshop, the template with its placeholders (a line whose
 * data is missing or switched off is left out) and the SMS length.
 *
 * Also the two addresses of a workshop (owner, 2026-11-04): the registered seat ("Székhely", invoices and legal
 * texts) and the workshop address ("Műhely címe", where the car is). Settings keys: `seatAddress`,
 * `workshopAddressSameAsSeat` (default ticked) and `workshopAddress`.
 */
import { currency } from "./money";

// ---- addresses ------------------------------------------------------------------------------------------

type General = Record<string, unknown>;
const text = (g: General, k: string) => {
  const v = g[k];
  return typeof v === "string" && v.trim() ? v.trim() : null;
};

/** The registered seat. Workshops from before 2026-11-04 had only `workshopAddress`; that counts as the seat too. */
export function seatAddressOf(general: General): string | null {
  return text(general, "seatAddress") ?? text(general, "workshopAddress");
}

/** Whether the workshop address is the seat (the tick, default on). */
export function siteSameAsSeat(general: General): boolean {
  const v = general.workshopAddressSameAsSeat;
  return !(v === false || v === "false");
}

/** Where the car is: the seat when the tick is on, otherwise the workshop address (the seat if that is empty). */
export function siteAddressOf(general: General): string | null {
  if (siteSameAsSeat(general)) return seatAddressOf(general);
  return text(general, "workshopAddress") ?? seatAddressOf(general);
}

/** The workshop address when it differs from the seat (printed as "where the car is received"), else null. */
export function siteAddressIfDifferent(general: General): string | null {
  const site = siteAddressOf(general);
  const seat = seatAddressOf(general);
  return site && seat && site.replace(/\s+/g, " ").toLowerCase() !== seat.replace(/\s+/g, " ").toLowerCase() ? site : null;
}

// ---- route links ----------------------------------------------------------------------------------------

export const googleMapsRoute = (address: string) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address.trim())}`;
export const wazeRoute = (address: string) => `https://waze.com/ul?q=${encodeURIComponent(address.trim())}&navigate=yes`;

export function routeLinks(address: string | null | undefined): { google: string; waze: string } | null {
  const a = address?.trim();
  return a ? { google: googleMapsRoute(a), waze: wazeRoute(a) } : null;
}

// ---- amount -----------------------------------------------------------------------------------------------

/** The step the notice rounds to, in minor units: 100 Ft for HUF, one whole unit (e.g. 1 EUR) otherwise. */
export function readyAmountStep(currencyCode: string): number {
  return currencyCode === "HUF" ? 100 : 10 ** currency(currencyCode).decimals;
}

/** The gross total rounded to the step, half up (48 449 Ft → 48 400 Ft, 48 450 Ft → 48 500 Ft). Never negative. */
export function readyAmount(grossMinor: number, currencyCode: string): number {
  const step = readyAmountStep(currencyCode);
  const g = Math.max(0, Math.round(grossMinor));
  return Math.floor((g + step / 2) / step) * step;
}

/** The rounded amount for the text, without decimals ("48 500 Ft"); the "approx." word comes from the locale. */
export function formatReadyAmount(roundedMinor: number, currencyCode: string, locale: string): string {
  const c = currency(currencyCode);
  return new Intl.NumberFormat(locale, { style: "currency", currency: c.code, minimumFractionDigits: 0, maximumFractionDigits: 0 })
    .format(roundedMinor / 10 ** c.decimals);
}

// ---- opening hours ----------------------------------------------------------------------------------------

/** ISO weekdays: 1 Monday ... 7 Sunday. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export interface DayHours {
  from: string;
  to: string;
}
/** A day without hours is closed. */
export type OpeningHours = Record<Weekday, DayHours | null>;

/** What the editor starts with: Monday to Friday 8:00 to 17:00, the weekend closed. */
export const DEFAULT_OPENING_HOURS: OpeningHours = {
  1: { from: "08:00", to: "17:00" }, 2: { from: "08:00", to: "17:00" }, 3: { from: "08:00", to: "17:00" },
  4: { from: "08:00", to: "17:00" }, 5: { from: "08:00", to: "17:00" }, 6: null, 7: null,
};

export const isTime = (s: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(s);

/** A day is right when both times are given and it opens before it closes. */
export const dayHoursValid = (d: DayHours | null) => d === null || (isTime(d.from) && isTime(d.to) && d.from < d.to);

/** The days whose hours are wrong (marked red, the save button grey). */
export function openingHoursProblems(h: OpeningHours): Weekday[] {
  return WEEKDAYS.filter((d) => !dayHoursValid(h[d]));
}

/** Stored as text in the settings: "1=08:00-17:00;2=...;6=;7=" (an empty day is closed). */
export function serializeOpeningHours(h: OpeningHours): string {
  return WEEKDAYS.map((d) => `${d}=${h[d] ? `${h[d]!.from}-${h[d]!.to}` : ""}`).join(";");
}

export function parseOpeningHours(s: unknown): OpeningHours | null {
  if (typeof s !== "string" || !s.trim()) return null;
  const out = { 1: null, 2: null, 3: null, 4: null, 5: null, 6: null, 7: null } as OpeningHours;
  for (const part of s.split(";")) {
    const m = /^([1-7])=(?:(\d{2}:\d{2})-(\d{2}:\d{2}))?$/.exec(part.trim());
    if (!m) return null;
    const day = Number(m[1]) as Weekday;
    out[day] = m[2] && m[3] ? { from: m[2], to: m[3] } : null;
  }
  return openingHoursProblems(out).length ? null : out;
}

/** "08:00" → "8:00" (a leading zero is noise in a message). */
const shortTime = (t: string) => t.replace(/^0(\d)/, "$1");

/**
 * Consecutive days with the same hours as one row: [{ days: "H–P", hours: "8:00–17:00" }, { days: "Szo", ... }].
 * `dayNames` are the short names Monday first. Closed days are left out.
 */
export function openingHoursRows(h: OpeningHours, dayNames: readonly string[]): { days: string; hours: string }[] {
  const rows: { first: Weekday; last: Weekday; hours: string }[] = [];
  for (const d of WEEKDAYS) {
    const v = h[d];
    if (!v) continue;
    const hours = `${shortTime(v.from)}–${shortTime(v.to)}`;
    const prev = rows.at(-1);
    if (prev && prev.last === d - 1 && prev.hours === hours) prev.last = d;
    else rows.push({ first: d, last: d, hours });
  }
  return rows.map((r) => ({
    days: r.first === r.last ? dayNames[r.first - 1]! : `${dayNames[r.first - 1]}${r.last - r.first === 1 ? ", " : "–"}${dayNames[r.last - 1]}`,
    hours: r.hours,
  }));
}

/** One line for a message: "H–P 8:00–17:00, Szo 8:00–12:00"; empty when every day is closed. */
export function formatOpeningHours(h: OpeningHours, dayNames: readonly string[]): string {
  return openingHoursRows(h, dayNames).map((r) => `${r.days} ${r.hours}`).join(", ");
}

/** The opening hours that go into the notice: only when given (switch on), valid and asked for in the notice. */
export function noticeOpeningHours(general: General): OpeningHours | null {
  const on = general.openingHoursOn === true || general.openingHoursOn === "true";
  const inNotice = !(general.readyNoticeHours === false || general.readyNoticeHours === "false");
  return on && inNotice ? parseOpeningHours(general.openingHours) : null;
}

/** Whether the amount goes into the notice ("Összeg az értesítésben", default on). */
export function noticeShowsAmount(general: General): boolean {
  return !(general.readyNoticeAmount === false || general.readyNoticeAmount === "false");
}

// ---- template ---------------------------------------------------------------------------------------------

export const READY_PLACEHOLDERS = ["name", "plate", "vehicle", "number", "amount", "hours", "address", "route", "waze", "link", "workshop", "phone"] as const;
export type ReadyPlaceholder = (typeof READY_PLACEHOLDERS)[number];
export type ReadyValues = Partial<Record<ReadyPlaceholder, string | null>>;

const PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/g;

/** Placeholders of a template the program does not know (shown as a warning in Settings). */
export function unknownPlaceholders(template: string): string[] {
  const known = new Set<string>(READY_PLACEHOLDERS);
  return [...new Set([...template.matchAll(PLACEHOLDER)].map((m) => m[1]!).filter((k) => !known.has(k)))];
}

/**
 * Fills the template line by line. A line with a placeholder whose value is missing (no data, or switched off) is
 * left out entirely, so no "Átveheti: ." remains; an unknown placeholder counts as missing. Blank lines are kept
 * single, the text is trimmed.
 */
export function fillTemplate(template: string, values: ReadyValues): string {
  const lines: string[] = [];
  for (const line of template.replace(/\r\n?/g, "\n").split("\n")) {
    let missing = false;
    const filled = line.replace(PLACEHOLDER, (_, k: string) => {
      const v = (values as Record<string, string | null | undefined>)[k];
      if (v === null || v === undefined || v.trim() === "") {
        missing = true;
        return "";
      }
      return v.trim();
    });
    if (missing) continue;
    if (filled.trim() === "" && (lines.length === 0 || lines.at(-1) === "")) continue;
    lines.push(filled.trimEnd());
  }
  while (lines.at(-1) === "") lines.pop();
  return lines.join("\n");
}

export interface ReadyNoticeData {
  /** The customer's name; empty → `fallbackName` (the formal "Ügyfelünk"). */
  customerName: string | null;
  fallbackName: string;
  plate: string | null;
  vehicle: string | null;
  number: string;
  /** "kb. 48 500 Ft" (already with the approx. word), or null when switched off. */
  amountText: string | null;
  /** One line of opening hours, or null when not given or switched off. */
  hoursText: string | null;
  /** The workshop address (where the car is). */
  address: string | null;
  /** The customer's page (#ready/<token>). */
  link: string | null;
  workshop: string;
  phone: string | null;
}

export function readyValues(d: ReadyNoticeData): ReadyValues {
  const route = routeLinks(d.address);
  return {
    name: d.customerName?.trim() || d.fallbackName,
    // a work order without a vehicle still says something: the vehicle's name or the work order's number
    plate: d.plate?.trim() || d.vehicle?.trim() || d.number,
    vehicle: d.vehicle,
    number: d.number,
    amount: d.amountText,
    hours: d.hoursText || null,
    address: d.address,
    route: route?.google ?? null,
    waze: route?.waze ?? null,
    link: d.link,
    workshop: d.workshop,
    phone: d.phone,
  };
}

// ---- SMS --------------------------------------------------------------------------------------------------

const GSM_BASIC = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXTENDED = "^{}\\[~]|€\f";

/** Length and number of SMS parts: 160 / 153 characters in the GSM alphabet, 70 / 67 otherwise (e.g. "á", "ő"). */
export function smsSegments(text: string): { gsm: boolean; length: number; segments: number } {
  let gsm = true;
  let length = 0;
  for (const ch of text) {
    if (GSM_BASIC.includes(ch)) length += 1;
    else if (GSM_EXTENDED.includes(ch)) length += 2;
    else {
      gsm = false;
      break;
    }
  }
  if (!gsm) length = text.length;
  const single = gsm ? 160 : 70;
  const part = gsm ? 153 : 67;
  return { gsm, length, segments: length === 0 ? 0 : length <= single ? 1 : Math.ceil(length / part) };
}

const ASCII_LIKE: Record<string, string> = { "„": "\"", "”": "\"", "“": "\"", "’": "'", "‘": "'", "–": "-", "—": "-", "…": "...", " ": " ", " ": " " };

/** The text in the GSM alphabet: letters the alphabet lacks lose their accent (á → a, ő → o), quotes become plain. */
export function toGsmText(text: string): string {
  let out = "";
  for (const ch of text) {
    if (GSM_BASIC.includes(ch) || GSM_EXTENDED.includes(ch)) out += ch;
    else if (ASCII_LIKE[ch]) out += ASCII_LIKE[ch];
    else {
      const plain = ch.normalize("NFD").replace(/[̀-ͯ]/g, "");
      out += [...plain].every((c) => GSM_BASIC.includes(c)) ? plain : "?";
    }
  }
  return out;
}

/**
 * The SMS as sent: the template filled; the link is always in it (added as the last line when the template has
 * none); when it would be longer than `maxSegments` parts with accents, it goes without accents (owner: one or two
 * SMS). Returns the text and its parts.
 */
export function buildReadySms(template: string, values: ReadyValues, maxSegments = 2): { text: string; segments: number; plain: boolean } {
  let body = fillTemplate(template, values);
  if (values.link && !body.includes(values.link)) body = `${body}\n${values.link}`.trim();
  const first = smsSegments(body);
  if (first.segments <= maxSegments) return { text: body, segments: first.segments, plain: false };
  const plain = toGsmText(body);
  // a script the GSM alphabet cannot write (e.g. Cyrillic) stays as it is, in more parts
  if ((plain.match(/\?/g) ?? []).length > (body.match(/\?/g) ?? []).length) return { text: body, segments: first.segments, plain: false };
  return { text: plain, segments: smsSegments(plain).segments, plain: true };
}
